import {
  AdminImageInput,
  AdminInput,
  AdminSwitch,
  Button,
  Disclaimer,
  Div,
  H3,
  H5,
  Modal,
  P,
  PropsModal,
  Separator,
  SuccessToast,
  isAdmin,
  isPartner,
  lightTheme,
  updatePrintingSettingsWithImage,
  updateLocationPrintingSettings,
  deleteFooterImage,
  reportError,
} from '@vgrubs/components';
import { useEffect, useState } from 'react';
import { PlaceholderImage } from './Settings.styled';
import { PrintingSettingsResponse } from 'components/src/types/api/location.types';

export const PrintingSettingsModal = ({
  modalIsOpen,
  closeModal,
  setLoading,
  data,
  id,
  handleGetSlip,
}: PropsModal & {
  handleGetSlip: (type: 'front' | 'back') => Promise<void>;
  modalIsOpen: boolean;
  data: PrintingSettingsResponse | null;
  id: number;
}) => {
  const [message, setMessage] = useState('');
  const [frontCopies, setFrontCopies] = useState(1);
  const [backCopies, setBackCopies] = useState(1);
  const [image, setImage] = useState<string | Blob | File | null>(null);
  const [deleteImageModal, setDeleteImageModal] = useState(false);
  const [toggle, setToggle] = useState(false);
  const [includePreparationNotes, setIncludePreparationNotes] = useState(false);
  useEffect(() => {
    if (data) {
      setMessage(data.message ?? '');
      setFrontCopies(data.frontCopies);
      setBackCopies(data.backCopies);
      setImage(data.image ?? '');
      setIncludePreparationNotes(data.includePreparationNotes);
      if (isPartner) {
        setToggle(data.onlyBrandReceipt);
      }
    }
  }, [data]);

  async function handlePrintingSettings() {
    if (
      message === data?.message &&
      frontCopies === data.frontCopies &&
      backCopies === data.backCopies &&
      toggle === data.onlyBrandReceipt &&
      includePreparationNotes === data.includePreparationNotes &&
      (image === data.image || image === '')
    ) {
      return closeModal?.();
    }

    const body = {
      message,
      frontCopies,
      backCopies,
      onlyBrandReceipt: toggle,
      includePreparationNotes,
    };
    try {
      const formData = new FormData();

      if (isAdmin && typeof image !== 'string' && image !== null) {
        setLoading?.(true);

        formData.append('image', image);
        formData.append('printingData', JSON.stringify(body));

        const { data } = await updatePrintingSettingsWithImage(id, formData);
        SuccessToast(data.message);
      } else {
        setLoading?.(true);
        const res = await updateLocationPrintingSettings(id, body);
        SuccessToast(res.data.message);
      }

      closeModal?.();

      setLoading?.(false);
    } catch (error) {
      reportError(error);
      setLoading?.(false);
    }
  }

  async function handleDeleteImage() {
    try {
      setLoading?.(true);
      const { data } = await deleteFooterImage(id);
      setImage(null);
      SuccessToast(data.message);
      setDeleteImageModal(false);
      setLoading?.(false);
    } catch (error) {
      reportError(error);
      setLoading?.(false);
    }
  }

  return (
    <>
      <Modal
        isOpen={modalIsOpen}
        closeModal={closeModal}
        logo={false}
        header="Printing Settings"
      >
        {isAdmin && (
          <Div $mb="24">
            <AdminImageInput
              image={image}
              onChange={(img: string | Blob | File | null) => setImage(img)}
              label="Footer Image"
              placeholder={PlaceholderImage as any}
              onDelete={() => setDeleteImageModal(true)}
            />
          </Div>
        )}

        <AdminInput
          label="Footer Message"
          type="textarea"
          placeholder="Enter your message here.."
          value={message}
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
            setMessage(e.target.value)
          }
        />
        <Div $flex $gap="16px" $mt="24">
          <AdminInput
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setFrontCopies(Number(e.target.value))
            }
            value={frontCopies}
            label="Front Receipts"
            type="number"
            $textCenter
          />
          <AdminInput
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setBackCopies(Number(e.target.value))
            }
            value={backCopies}
            label="Back Receipts"
            type="number"
            $textCenter
          />
        </Div>
        <Disclaimer $m="16px 0 24px 0">
          The total number of receipts to print.
        </Disclaimer>

        {isPartner && (
          <>
            <Separator $m="24px 0" />
            <Div $flex $alignCenter $between>
              <H5 $m="0" $bold>
                Print only VR orders
              </H5>

              <AdminSwitch
                checked={toggle}
                onChange={() => setToggle((prev) => !prev)}
              />
            </Div>
            <Separator $m="24px 0" />
          </>
        )}

        {!isPartner && <Separator $m="16px 0" />}
        <Div $flex $alignCenter $between $mb="32">
          <H5 $m="0" $bold>
            Print Preparation Notes
          </H5>
          <AdminSwitch
            checked={includePreparationNotes}
            onChange={() => setIncludePreparationNotes((prev) => !prev)}
          />
        </Div>
        <Button
          $selected
          $gray
          $w="100%"
          onClick={() => handleGetSlip('front')}
        >
          Manage Front Slip
        </Button>
        <Button
          $selected
          $gray
          $w="100%"
          $mt="16"
          onClick={() => handleGetSlip('back')}
        >
          Manage Kitchen Slip
        </Button>
        <Button $selected $w="100%" $mt="16" onClick={handlePrintingSettings}>
          Update
        </Button>
      </Modal>

      <Modal
        isOpen={deleteImageModal}
        closeModal={() => setDeleteImageModal(false)}
        logo={false}
        zIndex={110}
      >
        <H3 $mb="24" $color={lightTheme.primary}>
          Are you sure
        </H3>
        <P $mb="40">Are you sure you want to delete footer image?</P>

        <Button $selected $w="100%" $mb="8" onClick={handleDeleteImage}>
          Confirm
        </Button>

        <Button
          $selected
          $w="100%"
          $gray
          onClick={() => setDeleteImageModal(false)}
        >
          Cancel
        </Button>
      </Modal>
    </>
  );
};
